import React, { useState, useRef, FormEvent } from "react";
import { useTranscribe } from "../hooks/useTranscribe";

interface UploadProps {
  onSubmit?: (data: { url: string; toneSamples: string; transcript?: string }) => void;
  loading?: boolean;
  onBack?: () => void;
}

const Upload: React.FC<UploadProps> = ({ onSubmit, loading: externalLoading, onBack }) => {
  const [activeTab, setActiveTab] = useState<"youtube" | "upload">("youtube");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [toneSamples, setToneSamples] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Use our custom hook for transcription
  const { transcribe, isLoading: transcribeLoading, error: transcribeError } = useTranscribe();
  
  // Combine loading states
  const loading = externalLoading || transcribeLoading;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] || null);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setSelectedFile(e.dataTransfer.files?.[0] || null);
    setError(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!onSubmit) return;
    setError(null);
    
    if (activeTab === "youtube" && !youtubeUrl) {
      setError("Please enter a YouTube URL");
      return;
    }

    if (activeTab === "upload" && !selectedFile) {
      setError("Please select a file");
      return;
    }
    
    try {
      if (activeTab === "youtube") {
        // Call the transcribe API
        const transcript = await transcribe(youtubeUrl);
        
        // Pass the transcript along with other data
        onSubmit({
          url: youtubeUrl,
          toneSamples,
          transcript
        });
      } else {
        // Handle file upload (not implemented in API yet)
        onSubmit({
          url: selectedFile?.name || "",
          toneSamples
        });
      }
    } catch (err) {
      // Error is already set by the hook
      console.error("Submission error:", err);
    }
  };

  return (
    <div className="content-card">
      <div className="form-header">
        <div className="back-button-container">
          {onBack && (
            <button 
              type="button" 
              className="back-button"
              onClick={onBack}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor" 
                className="back-button-icon"
              >
                <path 
                  fillRule="evenodd" 
                  d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" 
                  clipRule="evenodd" 
                />
              </svg>
              Back
            </button>
          )}
        </div>
        <h2 className="form-title">Create Content</h2>
        <p className="form-subtitle">Turn your long-form content into engaging social media posts</p>
      </div>

      <form onSubmit={handleSubmit}>
        {(error || transcribeError) && (
          <div className="error-message" style={{ color: 'red', marginBottom: '15px', padding: '10px', backgroundColor: '#ffeeee', borderRadius: '4px' }}>
            {error || transcribeError}
          </div>
        )}
        <div className="tabs-container">
          <div className="tab-buttons">
            <button
              type="button"
              className={`tab-button ${activeTab === 'youtube' ? 'active' : ''}`}
              onClick={() => setActiveTab('youtube')}
            >
              YouTube URL
              {activeTab === 'youtube' && <span className="tab-underline"></span>}
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              Upload Video/Audio
              {activeTab === 'upload' && <span className="tab-underline"></span>}
            </button>
          </div>
        </div>

        {activeTab === 'youtube' ? (
          <div className="upload-area">
            <div className="upload-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="icon-svg">
                <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="upload-title">Enter a YouTube URL</h3>
            <p className="upload-subtitle">Paste a link to any YouTube video you want to repurpose</p>
            
            <div className="youtube-input">
              <input
                id="youtube-url"
                type="text"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        ) : (
          <div 
            className="upload-area"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleUploadClick}
          >
            <div className="upload-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="icon-svg">
                <path fillRule="evenodd" d="M11.47 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1-1.06 1.06l-3.22-3.22V16.5a.75.75 0 0 1-1.5 0V4.81L8.03 8.03a.75.75 0 0 1-1.06-1.06l4.5-4.5ZM3 15.75a.75.75 0 0 1 .75.75v2.25a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5a.75.75 0 0 1 1.5 0v2.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V16.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="upload-title">Upload a video or audio file</h3>
            <p className="upload-subtitle">Take a video or audio file from your device</p>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={loading}
              className="sr-only"
              accept="video/*,audio/*"
            />
            
            {selectedFile ? (
              <div className="selected-file">
                <p>Selected: {selectedFile.name}</p>
              </div>
            ) : (
              <button
                type="button"
                className="upload-button"
                disabled={loading}
              >
                Choose File
              </button>
            )}
          </div>
        )}

        <div className="input-group">
          <label htmlFor="tone-samples">Your Writing Style (Optional)</label>
          <textarea
            id="tone-samples"
            placeholder="Paste 2-3 examples of your social media posts to match your unique voice"
            value={toneSamples}
            onChange={(e) => setToneSamples(e.target.value)}
            disabled={loading}
            rows={4}
          />
          <p className="form-hint">
            Example: Just launched our new feature! 🚀 Can't wait for you to try it out and share your thoughts. #ProductDevelopment #Innovation
          </p>
        </div>

        <button 
          type="submit" 
          className="cta-button full-width"
          disabled={loading || (activeTab === "youtube" && !youtubeUrl) || (activeTab === "upload" && !selectedFile)}
        >
          {loading ? 'Processing...' : 'Generate Posts'}
        </button>
      </form>
    </div>
  );
};

export default Upload;
